import { computed, reactive, ref, watch } from "vue";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  deleteField,
  serverTimestamp,
} from "@/services/legacyBrowserPersistenceBoundary";
import {
  Contract,
  BrowserProvider,
  formatEther,
  keccak256,
  toUtf8Bytes,
} from "ethers";
import { toast } from "vue3-toastify";

import { nftConfig, assertMarketplaceConfig, getMarketplaceAddress, getCollectionFactoryAddress } from "@/config/nft";
import { pinListingMetadata } from "@/services/nft/metadataPinningService";
import { pinListingImage } from "@/services/nft/imagePinningService";
import { classifyImageSize, shouldAttemptIpfsImagePin, formatBytes } from "@/config/mediaLimits";
import { buildListingMetadata, addSaleMetadata, addCollectionMetadata } from "@/services/nft/listingMetadataBuilder";
import { deployCollection, mintToCollection, burnNFT as burnCollectionNFT } from "@/services/nft/collectionContractService";
import { saveUserCollection } from "@/services/nft/userCollectionsService";
import { generateTextPoster } from "@/utils/generateTextPoster";

const marketplaceAbi = [
  "function mintTo(address to, string tokenURI_, uint96 royaltyBps) external payable returns (uint256)",
  "function burn(uint256 tokenId) external",
  "function nextTokenId() view returns (uint256)",
  "function mintFeeWei() view returns (uint256)",
  "function platformFeeBps() view returns (uint96)",
  "function platformFeeRecipient() view returns (address)",
  "function platformRoyaltyShareBps() view returns (uint96)",
  "function treasury() view returns (address)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "event Minted(address indexed creator, address indexed recipient, uint256 indexed tokenId, string tokenURI, uint256 feePaid)",
  "event Burned(address indexed owner, uint256 indexed tokenId, address indexed royaltySplitter)",
  "event RoyaltySplitterDeployed(uint256 indexed tokenId, address indexed splitterAddress, address indexed creator, uint96 creatorShareBps, uint96 platformShareBps)",
];

const MINTED_EVENT_TOPIC = keccak256(
  toUtf8Bytes("Minted(address,address,uint256,string,uint256)")
);

const parseMintFeeWeiOrThrow = () => {
  try {
    return BigInt(nftConfig.mintFeeWei || "0");
  } catch (feeError) {
    console.error("Invalid mint fee configuration", feeError);
    throw new Error("Mint fee configuration invalid. Contact support.");
  }
};

const resolveMaxRoyaltyBps = () => {
  const raw = Number(nftConfig.maxRoyaltyBps || 0);
  if (Number.isNaN(raw) || raw < 0) {
    return 0;
  }
  return Math.min(raw, 5_000);
};

const clampRoyaltyBps = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric < 0) {
    return 0;
  }
  const max = resolveMaxRoyaltyBps();
  if (max > 0) {
    return Math.min(numeric, max);
  }
  return numeric;
};

const resolveDefaultRoyaltyBps = () => {
  const candidate =
    typeof nftConfig.defaultRoyaltyBps === "number"
      ? nftConfig.defaultRoyaltyBps
      : Number(nftConfig.defaultRoyaltyBps || 0);
  return clampRoyaltyBps(candidate);
};

const ensureWalletConnected = async (desiredChainId = null, existingProvider = null) => {
  // Try the provider-owned WalletConnect session, then fall back to window.ethereum.
  let eip1193Provider = existingProvider || null;

  if (!eip1193Provider) {
    try {
      const { default: WalletConnectAdapter } = await import(
        "@/services/externalWallet/WalletConnectAdapter"
      );
      const wcAdapter = new WalletConnectAdapter();
      const ok = await wcAdapter.initialize();
      if (
        ok &&
        wcAdapter.hasPersistedSession() &&
        wcAdapter.provider &&
        wcAdapter.accounts?.length
      ) {
        eip1193Provider = wcAdapter.provider;
        console.log("[ensureWalletConnected] Using WalletConnect provider");
      }
    } catch (wcErr) {
      console.warn("[ensureWalletConnected] WalletConnect session restore failed, falling back", wcErr);
    }

    if (!eip1193Provider && window.ethereum) {
      eip1193Provider = window.ethereum;
      console.log("[ensureWalletConnected] Using injected provider (MetaMask)");
    }
  } // end if (!eip1193Provider) — skip detection when caller supplied provider

  if (!eip1193Provider) {
    throw new Error("No wallet detected. Connect a wallet to continue.");
  }

  // Use provided chainId or default to Base
  const targetChainId = (desiredChainId || nftConfig.chainId || "0x2105").toLowerCase();

  const accounts = await eip1193Provider.request({
    method: "eth_requestAccounts",
  });
  if (!accounts || accounts.length === 0) {
    throw new Error("Wallet connection is required to mint.");
  }

  let currentChainId = await eip1193Provider.request({
    method: "eth_chainId",
  });
  currentChainId = (typeof currentChainId === "number"
    ? "0x" + currentChainId.toString(16)
    : currentChainId
  )?.toLowerCase();

  if (targetChainId && currentChainId !== targetChainId) {
    // Determine chain params based on target
    let chainParams;
    if (targetChainId === "0x2105") {
      chainParams = nftConfig.baseChainParams;
    } else if (targetChainId === "0x1") {
      chainParams = nftConfig.ethereumChainParams;
    }

    try {
      await eip1193Provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: targetChainId }],
      });
    } catch (switchError) {
      if (switchError?.code === 4902 && chainParams) {
        await eip1193Provider.request({
          method: "wallet_addEthereumChain",
          params: [chainParams],
        });
      } else {
        const chainName = targetChainId === "0x1" ? "Ethereum" : "Base";
        throw new Error(
          `Switch to the ${chainName} network to continue this mint.`
        );
      }
    }
  }

  const provider = new BrowserProvider(eip1193Provider);
  const signer = await provider.getSigner();
  const account = await signer.getAddress();

  return { provider, signer, account, eip1193Provider };
};

const extractTokenIdFromReceipt = (receipt, contractInterface) => {
  if (!receipt) return null;

  if (contractInterface) {
    for (const log of receipt.logs || []) {
      try {
        const parsed = contractInterface.parseLog(log);
        if (parsed && parsed.name === "Minted") {
          const tokenIdValue = parsed.args?.tokenId;
          if (tokenIdValue !== undefined && tokenIdValue !== null) {
            return tokenIdValue.toString();
          }
        }
      } catch (err) {
        // Ignore parse failures for logs that don't belong to this contract
      }
    }
  }

  for (const log of receipt.logs || []) {
    const topics = log?.topics;
    if (Array.isArray(topics) && topics.length >= 4 && topics[0] === MINTED_EVENT_TOPIC) {
      const tokenIdTopic = topics[3];
      if (typeof tokenIdTopic === "string" && tokenIdTopic.length > 2) {
        try {
          return BigInt(tokenIdTopic).toString();
        } catch (parseError) {
          console.warn("Failed to parse tokenId topic", parseError);
        }
      }
    }
  }

  return null;
};

/**
 * Extract RoyaltySplitter address from a RoyaltySplitterDeployed event in the receipt.
 * Only present for shared UnitMarketplace mints (not custom UnitCollection mints).
 */
function extractSplitterAddressFromReceipt(receipt, contractInterface) {
  if (!receipt?.logs) return null;
  for (const log of receipt.logs) {
    try {
      const parsed = contractInterface.parseLog(log);
      if (parsed && parsed.name === "RoyaltySplitterDeployed") {
        return parsed.args.splitterAddress || parsed.args[1]; // indexed arg
      }
    } catch (e) {
      // Not from this contract interface
    }
  }
  return null;
}

const normaliseListingMedia = (listing) => {
  const imageSet = new Set();
  const addUrl = (url) => {
    if (typeof url === "string" && url.length > 0 && !url.startsWith("blob:")) {
      imageSet.add(url);
    }
  };

  if (Array.isArray(listing.images)) {
    listing.images.forEach(addUrl);
  }
  if (Array.isArray(listing.additionalImages)) {
    listing.additionalImages.forEach(addUrl);
  }
  if (Array.isArray(listing.imagePreviews)) {
    listing.imagePreviews.forEach(addUrl);
  }

  addUrl(listing.mediaUrl);
  addUrl(listing.thumbnail);
  addUrl(listing.thumbnailUrl);
  addUrl(listing.posterUrl);
  addUrl(listing.coverUrl);
  addUrl(listing.coverImage);
  addUrl(listing.previewUrl);
  addUrl(listing.documentPreviewUrl);

  if (Array.isArray(listing.documentUrls)) {
    listing.documentUrls.forEach(addUrl);
  }

  listing.images = Array.from(imageSet);

  if (!listing.mediaUrl) {
    const preferredMedia =
      listing.processedVideoUrl ||
      listing.videoUrl ||
      listing.processedAudioUrl ||
      listing.audioUrl ||
      listing.media;
    if (preferredMedia) {
      addUrl(preferredMedia);
      listing.mediaUrl = preferredMedia;
    }
  }

  if (!listing.mediaUrl && listing.images.length > 0) {
    listing.mediaUrl = listing.images[0];
  }

  if (typeof listing.thumbnailIndex !== "number" && listing.images.length > 0) {
    listing.thumbnailIndex = 0;
  }
};

const resolveListingSource = (dbInstance, item) => {
  if (!item || !item.id) {
    throw new Error("Missing listing identifier.");
  }

  const sanitizePath = (raw) => {
    if (typeof raw !== "string" || raw.length === 0) return null;
    return raw;
  };

  const preferredPath =
    sanitizePath(item._originalPath) ||
    sanitizePath(item.originalPath) ||
    sanitizePath(item.listingPath) ||
    null;

  if (preferredPath) {
    const parts = preferredPath.split("/").filter(Boolean);
    if (parts.length >= 2) {
      const [collectionName, docId] = parts;
      return {
        collectionName,
        docId,
        listingPath: `${collectionName}/${docId}`,
        mediaType: String(item.mediaType || "").toLowerCase() || null,
        docRef: doc(dbInstance, collectionName, docId),
      };
    }
  }

  const derivedMediaType = String(item.mediaType || item.type || "")
    .toLowerCase()
    .trim();

  if (item.type === "product" || derivedMediaType === "product") {
    return {
      collectionName: "products",
      docId: item.id,
      listingPath: `products/${item.id}`,
      mediaType: "product",
      docRef: doc(dbInstance, "products", item.id),
    };
  }

  let collectionName = "images";
  if (derivedMediaType === "video") collectionName = "videos";
  else if (derivedMediaType === "audio") collectionName = "audios";
  else if (derivedMediaType === "documents") collectionName = "images";

  return {
    collectionName,
    docId: item.id,
    listingPath: `${collectionName}/${item.id}`,
    mediaType: derivedMediaType || null,
    docRef: doc(dbInstance, collectionName, item.id),
  };
};

const resolveAnimationUrl = (listing) => {
  const candidates = [
    listing.animation_url,
    listing.animationUrl,
    listing.processedVideoUrl,
    listing.videoUrl,
    listing.processedAudioUrl,
    listing.audioUrl,
    listing.mediaUrl,
  ];

  return candidates.find((url) => typeof url === "string" && url.length > 0);
};

// Helper to safely merge minted metadata into processed items cache
const markMinted = (processedItems, mintedCache, listingId, metadataFields = {}) => {
  if (listingId) {
    mintedCache[listingId] = true;
  }
  if (
    metadataFields &&
    typeof metadataFields === "object" &&
    listingId &&
    processedItems &&
    Array.isArray(processedItems.value)
  ) {
    const target = processedItems.value.find((entry) => entry.id === listingId);
    if (target) {
      Object.assign(target, metadataFields);
    }
  }
};

const isMintableItemType = (item) => {
  if (!item) return false;

  // Embed posts are never mintable, regardless of their mediaType
  if (item.embedHtml) return false;

  const type = String(item.type || "").toLowerCase();
  if (type === "product") return true;
  if (type === "media" || type.startsWith("media_")) return true;

  const mediaType = String(item.mediaType || "")
    .toLowerCase()
    .trim();

  if (!mediaType) return false;

  const mintableMedia = new Set([
    "media",
    "image",
    "video",
    "audio",
    "documents",
    "poster",
    "post",
    "tweet",
    "text",
  ]);

  if (mintableMedia.has(mediaType)) return true;
  if (mediaType.startsWith("media_")) return true;

  return false;
};

export function useMarketplaceMinting({ currentUserUid, processedItems } = {}) {
  const mintingStatus = reactive({});
  const mintedCache = reactive({});

  const markMintedFromRecord = (listingId, record = {}) => {
    if (!listingId) {
      return;
    }

    const effectiveStatus = String(
      record.status || record.onChainStatus || ""
    ).toLowerCase();
    const hasMintEvidence = Boolean(
      record.mintTxHash ||
        record.contractAddress ||
        record.marketplaceAddress ||
        record.tokenId
    );
    
    // CRITICAL: Only mark as minted if mintingStatus is "success" or legacy data without mintingStatus
    const mintingStatus = record.mintingStatus;
    const isMintingSuccess = !mintingStatus || mintingStatus === "success";
    
    // If mintingStatus exists and is not "success" (e.g., "pending" or "failed"), don't mark as minted
    if (!isMintingSuccess) {
      console.log(`[markMintedFromRecord] Skipping ${listingId} - mintingStatus: ${mintingStatus}`);
      return;
    }

    if (effectiveStatus !== "minted" && !hasMintEvidence) {
      return;
    }

    const metadataFields = {
      onChainStatus: effectiveStatus || "minted",
    };

    const assignIfPresent = (key, value, transform = (v) => v ?? null) => {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        metadataFields[key] = transform(value);
      }
    };

    assignIfPresent("metadataIpfsUri", record.metadataIpfsUri);
    assignIfPresent("metadataCid", record.metadataCid);
    assignIfPresent("metadataGatewayUrl", record.metadataGatewayUrl);
    assignIfPresent("metadataHash", record.metadataHash);
    assignIfPresent("tokenId", record.tokenId, (v) => (v ?? null));
    assignIfPresent("mintFeeWei", record.mintFeeWei, (v) => v ?? null);
    assignIfPresent("mintFeeEth", record.mintFeeEth, (v) => v ?? null);
    assignIfPresent("treasuryAddress", record.treasuryAddress, (v) => v ?? null);
    assignIfPresent("platformFeeBps", record.platformFeeBps);
    assignIfPresent(
      "platformFeeRecipient",
      record.platformFeeRecipient,
      (v) => v ?? null
    );
    assignIfPresent("swapFeeBps", record.swapFeeBps);
    assignIfPresent("creatorWallet", record.creatorWallet, (v) => v ?? null);
    assignIfPresent("royaltyBps", record.royaltyBps);
    assignIfPresent("mintTxHash", record.mintTxHash, (v) => v ?? null);
    assignIfPresent("mintedAt", record.mintedAt, (v) => v ?? null);

    if (
      metadataFields.treasuryAddress === undefined &&
      record.platformFeeRecipient
    ) {
      metadataFields.treasuryAddress = record.platformFeeRecipient;
    }

    const contractAddressCandidate =
      record.contractAddress || record.marketplaceAddress || record.nftContractAddress || null;
    if (contractAddressCandidate) {
      metadataFields.contractAddress = contractAddressCandidate;
    }

    markMinted(processedItems, mintedCache, listingId, metadataFields);
  };

  const tryMarkMintedFromItem = (item) => {
    if (!item || !item.id) return false;
    const mintedIndicators = [
      item.onChainStatus === "minted",
      item.contractAddress,
      item.tokenId,
      item.mintTxHash,
    ];
    if (mintedIndicators.some((indicator) => indicator)) {
      markMintedFromRecord(item.id, item);
      return true;
    }
    return false;
  };

  const hydrateMintedStateForItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    items.forEach((item) => {
      if (!item || !item.id) return;
      if (mintedCache[item.id]) return;
      tryMarkMintedFromItem(item);
    });
  };

  const showMintModal = ref(false);
  const pendingMint = ref(null);
  const mintModalAcknowledged = ref(false);
  const mintModalLoading = ref(false);

  if (processedItems && typeof processedItems === "object") {
    const currentProcessedItems = () =>
      Array.isArray(processedItems?.value) ? processedItems.value : [];
    const mintPresentationSignature = () =>
      currentProcessedItems().map((item) => [
        item?.id || null,
        item?.status || null,
        item?.onChainStatus || null,
        item?.mintingStatus || null,
        item?.mintTxHash || null,
        item?.contractAddress || null,
        item?.marketplaceAddress || null,
        item?.tokenId ?? null,
      ]);

    watch(
      mintPresentationSignature,
      () => hydrateMintedStateForItems(currentProcessedItems()),
      { immediate: true }
    );
  }

  const hasMintFeeConfigured = computed(() => {
    try {
      return BigInt(nftConfig.mintFeeWei || "0") > 0n;
    } catch {
      return false;
    }
  });

  const mintFeeEthDisplay = computed(() => {
    try {
      const wei = BigInt(nftConfig.mintFeeWei || "0");
      if (wei <= 0n) return "0";
      return formatEther(wei);
    } catch {
      return "0";
    }
  });

  const isItemOwnedByCurrentUser = (item) => {
    const uid = currentUserUid?.value || currentUserUid;
    if (item?.type === "media" && item?._legacyMediaCompatibility === true) {
      return false;
    }
    return item?.owner === uid;
  };

  const isItemMinted = (item) => {
    if (!item || !item.id) return false;
    if (mintedCache[item.id]) return true;
    return Boolean(
      item.contractAddress ||
        item.tokenId ||
        item.mintStatus === "minted" ||
        item.onChainStatus === "minted"
    );
  };

  const canMintItem = (item) => {
    if (!item || !isMintableItemType(item)) return false;
    if (!isItemOwnedByCurrentUser(item)) return false;
    if (item.id && mintingStatus[item.id]) return false;
    return !isItemMinted(item);
  };

  const cancelMint = () => {
    if (pendingMint.value?.listingId) {
      mintingStatus[pendingMint.value.listingId] = false;
    }
    showMintModal.value = false;
    mintModalAcknowledged.value = false;
    mintModalLoading.value = false;
    pendingMint.value = null;
  };

  const startMint = async (item) => {
    if (!item || !item.id) {
      toast.error("Unable to mint: missing listing information.");
      return;
    }

    const listingId = item.id;

    const uid = currentUserUid?.value || currentUserUid;
    if (!uid) {
      toast.error("You must be logged in to mint.");
      return;
    }

    if (!canMintItem(item)) {
      return;
    }

    let sourceInfo;
    mintingStatus[listingId] = true;
    mintModalAcknowledged.value = false;
    mintModalLoading.value = false;

    try {
      assertMarketplaceConfig();

      sourceInfo = resolveListingSource(null, item);

      const mintedDocRef = doc(null, "mintedListings", listingId);
      const [mintedSnap, sourceSnap] = await Promise.all([
        getDoc(mintedDocRef),
        getDoc(sourceInfo.docRef).catch((err) => {
          console.warn("Failed to fetch source listing", err);
          return null;
        }),
      ]);

      const existingMintData = mintedSnap.exists() ? mintedSnap.data() : null;
      const sourceData =
        sourceSnap && typeof sourceSnap.exists === "function" && sourceSnap.exists()
          ? sourceSnap.data()
          : {};
      const combined = { ...sourceData, ...item, id: sourceInfo.docId };

      normaliseListingMedia(combined);

      if (!combined.mediaType) {
        combined.mediaType =
          sourceInfo.mediaType ||
          item.mediaType ||
          (item.type === "media" ? "image" : item.type);
      }

      // Check if metadata already exists AND minting was successful
      // Allow retry if mintingStatus is "failed" or "pending" (interrupted)
      const existingMintingStatus = existingMintData?.mintingStatus;
      const isMintSuccessful = existingMintingStatus === "success";
      const canRetryMint = existingMintingStatus === "failed" || existingMintingStatus === "pending";

      if (existingMintData?.metadataIpfsUri && isMintSuccessful) {
        // Mint was successful, sync metadata and prevent re-mint
        await Promise.all([
          setDoc(
            mintedDocRef,
            {
              sourceCollection: sourceInfo.collectionName,
              sourcePath: sourceInfo.listingPath,
              mediaType: combined.mediaType || sourceInfo.mediaType || null,
              status: existingMintData.status || "minted",
              mintFeeWei:
                existingMintData.mintFeeWei || nftConfig.mintFeeWei || null,
              mintFeeEth: existingMintData.mintFeeEth || null,
              treasuryAddress:
                existingMintData.treasuryAddress ||
                nftConfig.treasuryAddress ||
                null,
              platformFeeBps:
                existingMintData.platformFeeBps !== undefined
                  ? existingMintData.platformFeeBps
                  : nftConfig.platformFeeBps,
              platformFeeRecipient:
                existingMintData.platformFeeRecipient ||
                nftConfig.treasuryAddress ||
                null,
              swapFeeBps:
                existingMintData.swapFeeBps !== undefined
                  ? existingMintData.swapFeeBps
                  : nftConfig.swapFeeBps,
              creatorWallet: existingMintData.creatorWallet || null,
              royaltyBps: existingMintData.royaltyBps || null,
            },
            { merge: true }
          ),
          setDoc(
            sourceInfo.docRef,
            {
              metadataIpfsUri: existingMintData.metadataIpfsUri,
              metadataCid: existingMintData.metadataCid,
              metadataGatewayUrl: existingMintData.metadataGatewayUrl,
              metadataHash: existingMintData.metadataHash,
              mintFeeWei:
                existingMintData.mintFeeWei || nftConfig.mintFeeWei || null,
              mintFeeEth: existingMintData.mintFeeEth || null,
              treasuryAddress:
                existingMintData.treasuryAddress ||
                nftConfig.treasuryAddress ||
                null,
              platformFeeBps:
                existingMintData.platformFeeBps !== undefined
                  ? existingMintData.platformFeeBps
                  : nftConfig.platformFeeBps,
              platformFeeRecipient:
                existingMintData.platformFeeRecipient ||
                nftConfig.treasuryAddress ||
                null,
              swapFeeBps:
                existingMintData.swapFeeBps !== undefined
                  ? existingMintData.swapFeeBps
                  : nftConfig.swapFeeBps,
              creatorWallet: existingMintData.creatorWallet || null,
            },
            { merge: true }
          ),
        ]);

        markMinted(processedItems, mintedCache, listingId, {
          metadataIpfsUri: existingMintData.metadataIpfsUri,
          metadataCid: existingMintData.metadataCid,
          metadataGatewayUrl: existingMintData.metadataGatewayUrl,
          metadataHash: existingMintData.metadataHash,
          metadataListingPath: sourceInfo.listingPath,
          contractAddress:
            existingMintData.contractAddress || nftConfig.marketplaceAddress,
          tokenId: existingMintData.tokenId || null,
          mintFeeWei:
            existingMintData.mintFeeWei || nftConfig.mintFeeWei || null,
          mintFeeEth: existingMintData.mintFeeEth || null,
          treasuryAddress:
            existingMintData.treasuryAddress || nftConfig.treasuryAddress || null,
          platformFeeBps:
            existingMintData.platformFeeBps !== undefined
              ? existingMintData.platformFeeBps
              : nftConfig.platformFeeBps,
          platformFeeRecipient:
            existingMintData.platformFeeRecipient ||
            nftConfig.treasuryAddress ||
            null,
          swapFeeBps:
            existingMintData.swapFeeBps !== undefined
              ? existingMintData.swapFeeBps
              : nftConfig.swapFeeBps,
          creatorWallet: existingMintData.creatorWallet || null,
          royaltyBps: existingMintData.royaltyBps || null,
        });
        toast.info("This listing already has NFT metadata.");
        mintingStatus[listingId] = false;
        return;
      }

      // If previous mint failed or was interrupted, clear stale metadata to allow retry
      if (canRetryMint && existingMintData?.metadataIpfsUri) {
        console.log(`[startMint] Previous mint ${existingMintingStatus} for ${listingId}, clearing stale metadata for retry`);
        toast.info("Previous mint attempt didn't complete. Preparing fresh mint...");
        
        // Clear metadata fields from both documents to allow fresh mint
        await Promise.all([
          setDoc(
            mintedDocRef,
            {
              metadataIpfsUri: deleteField(),
              metadataCid: deleteField(),
              metadataGatewayUrl: deleteField(),
              metadataHash: deleteField(),
              metadataPinnedAt: deleteField(),
              imageIpfsUri: deleteField(),
              imageCid: deleteField(),
              imageGatewayUrl: deleteField(),
              imageSha256: deleteField(),
              status: deleteField(),
              mintingStatus: "retrying",
              mintingRetryAt: serverTimestamp(),
            },
            { merge: true }
          ),
          setDoc(
            sourceInfo.docRef,
            {
              metadataIpfsUri: deleteField(),
              metadataCid: deleteField(),
              metadataGatewayUrl: deleteField(),
              metadataHash: deleteField(),
              metadataPinnedAt: deleteField(),
              imageIpfsUri: deleteField(),
              imageCid: deleteField(),
              imageGatewayUrl: deleteField(),
              imageSha256: deleteField(),
              onChainStatus: deleteField(),
              mintingStatus: "retrying",
              mintingRetryAt: serverTimestamp(),
            },
            { merge: true }
          ),
        ]);
      }

      const selectedRoyaltyBps = clampRoyaltyBps(
        existingMintData?.royaltyBps ?? resolveDefaultRoyaltyBps()
      );

      const animationUrl = resolveAnimationUrl(combined);

      const metadata = buildListingMetadata(listingId, combined, {
        organisationUid:
          combined.organisation ?? combined.organisationUid ?? combined.orgUID,
        creatorUid: combined.owner ?? combined.ownerUid ?? combined.creator,
        listingPath: sourceInfo.listingPath,
        mediaType: combined.mediaType || sourceInfo.mediaType,
        collectionName: sourceInfo.collectionName,
        animationUrl,
        royaltyBps: selectedRoyaltyBps,
      });

      if (!metadata.image) {
        // Posts without a display image — generate a text poster as the NFT image
        // Covers: text-only posts, audio posts (no cover art), document posts
        const mt = (combined.mediaType || "").toLowerCase();
        if (["text", "post", "poster", "tweet", "audio", "documents", "document", "doc"].includes(mt)) {
          const posterTitle = combined.itemName || combined.title || "";
          const posterBody = combined.caption || combined.description || combined.itemDescription || "";
          metadata.image = generateTextPoster({ title: posterTitle, body: posterBody });
          console.log(`[MINT] Generated text poster image for ${mt} post (no display image)`);
        } else {
          throw new Error(
            "Add at least one image or poster to the listing before minting."
          );
        }
      }

      const configuredMintFee = parseMintFeeWeiOrThrow();
      if (configuredMintFee > 0n && !nftConfig.treasuryAddress) {
        throw new Error(
          "Treasury address missing. Set VUE_APP_TREASURY_ADDRESS in the environment."
        );
      }

      const mintFeeEthText =
        configuredMintFee > 0n ? formatEther(configuredMintFee) : null;

      const previewCreatorWallet =
        existingMintData?.creatorWallet || combined.creatorWallet || "";

      const metadataForPreview = previewCreatorWallet
        ? {
            ...metadata,
            creatorWallet: previewCreatorWallet,
            properties: {
              ...(metadata.properties || {}),
              creatorWallet: previewCreatorWallet,
              royaltyBps: selectedRoyaltyBps,
            },
            royaltyBps: selectedRoyaltyBps,
          }
        : {
            ...metadata,
            properties: {
              ...(metadata.properties || {}),
              royaltyBps: selectedRoyaltyBps,
            },
            royaltyBps: selectedRoyaltyBps,
          };

      pendingMint.value = {
        listingId,
        item,
        combined,
        metadata: metadataForPreview,
        mintedDocRef,
        sourceInfo,
        configuredMintFee,
        mintFeeEthText,
        previewImage: metadata.image || combined.mediaUrl || "",
        mediaUrl: combined.mediaUrl || "",
        mediaType: combined.mediaType || "image",
        treasuryAddress: nftConfig.treasuryAddress || "",
        creatorWallet: previewCreatorWallet,
        royaltyBps: selectedRoyaltyBps,
        // Sale config (set by SimpleMintModal via setSaleConfig)
        saleConfig: null,
      };

      showMintModal.value = true;
    } catch (error) {
      console.error("Failed to prepare mint", error);
      toast.error(error?.message || "Mint failed. Try again later.");
      mintingStatus[listingId] = false;
      pendingMint.value = null;
    }
  };

  const confirmMint = async (existingProvider = null) => {
    if (!pendingMint.value) {
      return;
    }

    const {
      listingId,
      combined,
      metadata,
      mintedDocRef,
      sourceInfo,
      configuredMintFee,
      mintFeeEthText,
      treasuryAddress,
    } = pendingMint.value;
    const activeListingId = listingId;

    let currentChainId = null;
    try {
      mintModalLoading.value = true;

      let walletContext;
      try {
        // Allow user to mint on their current chain (Base or Ethereum)
        walletContext = await ensureWalletConnected(null, existingProvider); // null = don't force chain switch
      } catch (walletError) {
        // TODO(alchemy-smart-wallet): Replace with automated smart wallet provisioning flow via Alchemy.
        toast.info(
          "No wallet detected. We'll add a seamless smart wallet setup powered by Alchemy in an upcoming release."
        );
        return;
      }

      const { signer, account, eip1193Provider } = walletContext;

      // Get current chain ID to determine which contract address to use
      // Declared in outer scope so catch block can reference it for failure logging
      currentChainId = await eip1193Provider.request({ method: "eth_chainId" });
      if (typeof currentChainId === "number") {
        currentChainId = "0x" + currentChainId.toString(16);
      }
      const contractAddress = getMarketplaceAddress(currentChainId);
      
      console.log(`Minting on chain ${currentChainId} using contract ${contractAddress}`);

      if (pendingMint.value) {
        pendingMint.value.creatorWallet = account;
      }

      const appliedRoyaltyBps = clampRoyaltyBps(
        pendingMint.value?.royaltyBps ?? resolveDefaultRoyaltyBps()
      );

      let metadataWithCreator = {
        ...metadata,
        creatorWallet: account,
        royaltyBps: appliedRoyaltyBps,
        properties: {
          ...(metadata?.properties || {}),
          creatorWallet: account,
          royaltyBps: appliedRoyaltyBps,
        },
      };

      // Apply sale config if provided
      const saleConfig = pendingMint.value?.saleConfig;
      if (saleConfig && saleConfig.sale?.enabled) {
        metadataWithCreator = addSaleMetadata(metadataWithCreator, saleConfig.sale);
        
        // Apply collection if provided
        if (saleConfig.collection) {
          metadataWithCreator = addCollectionMetadata(metadataWithCreator, saleConfig.collection);
        }
      }

      // Apply custom NFT title if provided (overrides default "Unit Marketplace Item")
      if (saleConfig?.nftTitle && saleConfig.nftTitle.trim()) {
        metadataWithCreator = {
          ...metadataWithCreator,
          name: saleConfig.nftTitle.trim(),
        };
        console.log(`[MINT] Applied custom NFT title: "${saleConfig.nftTitle}"`);
      }

      if (pendingMint.value) {
        pendingMint.value.metadata = metadataWithCreator;
        pendingMint.value.royaltyBps = appliedRoyaltyBps;
      }

      // Attempt to pin primary image to IPFS (dual storage: Firebase + IPFS)
      let imagePinResult = null;
      let finalMetadata = { ...metadataWithCreator };
      try {
        const primaryImageUrl = metadataWithCreator.image;
        if (primaryImageUrl && typeof primaryImageUrl === "string") {
          // Fetch the already-uploaded Firebase image as a Blob to pass to callable
          const fetched = await fetch(primaryImageUrl);
            // Only proceed if fetch ok and reasonable size (< 20MB enforced server-side)
          if (fetched.ok) {
            const blob = await fetched.blob();
            const sizeBytes = blob.size;
            const sizeClassification = classifyImageSize(sizeBytes);
            if (sizeClassification === 'reject') {
              console.warn(`Primary image ${primaryImageUrl} size ${sizeBytes} exceeds hard limit; skipping IPFS pin.`);
            } else if (sizeClassification === 'warn') {
              toast?.info?.(`Large image (${formatBytes(sizeBytes)}). Pinning may take longer.`);
            }
            // Construct a File-like object (File API may not exist in all browsers; fallback to Blob with name)
            const fileName = primaryImageUrl.split("?")[0].split("/").pop() || "asset";
            let fileForPin = blob;
            try {
              // eslint-disable-next-line no-undef
              fileForPin = new File([blob], fileName, { type: blob.type || "application/octet-stream" });
            } catch { /* ignore if File constructor unsupported */ }
            if (shouldAttemptIpfsImagePin(sizeBytes)) {
              try {
                imagePinResult = await pinListingImage(listingId, fileForPin);
              } catch (imagePinError) {
                console.warn("Image IPFS pin attempt failed; continuing with Firebase URL", imagePinError);
              }
            }
            // Persist size info into metadata properties regardless of pin success
            metadataWithCreator.properties = {
              ...(metadataWithCreator.properties || {}),
              imageSizeBytes: sizeBytes,
              imageSizeHuman: formatBytes(sizeBytes),
            };
          }
        }
        if (imagePinResult && imagePinResult.ipfsUri) {
          finalMetadata = {
            ...finalMetadata,
            image: imagePinResult.ipfsUri, // canonical NFT image
            properties: {
              ...(finalMetadata.properties || {}),
              originalImageUrl: metadataWithCreator.image,
              imageIpfsUri: imagePinResult.ipfsUri,
              imageCid: imagePinResult.cid,
              imageGatewayUrl: imagePinResult.gatewayUrl,
              imageSha256: imagePinResult.sha256 || null,
              imageSizeBytes: finalMetadata?.properties?.imageSizeBytes,
              imageSizeHuman: finalMetadata?.properties?.imageSizeHuman,
            },
          };
        } else {
          finalMetadata = {
            ...finalMetadata,
            properties: {
              ...(finalMetadata.properties || {}),
              originalImageUrl: metadataWithCreator.image,
              imageSizeBytes: finalMetadata?.properties?.imageSizeBytes,
              imageSizeHuman: finalMetadata?.properties?.imageSizeHuman,
            },
          };
        }
      } catch (ipfsImageOuterError) {
        console.warn("Unexpected error during image IPFS pin flow; proceeding without IPFS image", ipfsImageOuterError);
      }

      const pinResponse = await pinListingMetadata(
        listingId,
        finalMetadata
      );

      // ═══════════════════════════════════════════════════════════════════
      // COLLECTION HANDLING: Deploy new or use existing custom collection
      // ═══════════════════════════════════════════════════════════════════
      let useCustomCollection = false;
      let customCollectionAddress = null;
      
      if (saleConfig?.collection) {
        if (saleConfig.collection.isNew && saleConfig.collection.name) {
          // Deploy a new collection first
          const factoryAddress = getCollectionFactoryAddress(currentChainId);
          if (factoryAddress) {
            toast.info(`Creating "${saleConfig.collection.name}" collection...`);
            try {
              // Build ERC-7572 contractURI with collection metadata
              // Includes OpenSea-compatible seller_fee_basis_points for royalty discovery
              const collectionMeta = JSON.stringify({
                name: saleConfig.collection.name,
                description: saleConfig.collection.description || "",
                image: "",
                external_link: window.location.origin,
                seller_fee_basis_points: appliedRoyaltyBps,
                fee_recipient: account,
              });
              const contractURI = `data:application/json;base64,${btoa(collectionMeta)}`;

              const deployResult = await deployCollection({
                signer,
                chainId: currentChainId,
                name: saleConfig.collection.name,
                royaltyBps: appliedRoyaltyBps,
                contractURI,
              });
              
              customCollectionAddress = deployResult.contractAddress;
              useCustomCollection = true;
              
              // Save to Firestore
              const userId = currentUserUid?.value || currentUserUid || null;
              if (userId) {
                await saveUserCollection({
                  userId,
                  walletAddress: account,
                  contractAddress: customCollectionAddress,
                  name: deployResult.name,
                  symbol: deployResult.symbol,
                  chainId: currentChainId,
                  royaltyBps: deployResult.royaltyBps,
                  contractURI,
                  txHash: deployResult.txHash,
                });
                console.log("[MINT] Saved collection to Firestore:", deployResult.name);
              } else {
                console.warn("[MINT] No userId available, collection not saved to Firestore");
              }
              
              toast.success(`Collection "${saleConfig.collection.name}" created!`);
            } catch (deployError) {
              console.error("[MINT] Failed to deploy collection:", deployError);
              toast.error(`Failed to create collection: ${deployError.message}`);
              throw deployError;
            }
          } else {
            // No factory on this chain, fall back to shared marketplace
            console.warn("[MINT] No collection factory on chain", currentChainId, "- using shared marketplace");
          }
        } else if (saleConfig.collection.address) {
          // Use existing custom collection
          customCollectionAddress = saleConfig.collection.address;
          useCustomCollection = true;
        }
      }
      // ═══════════════════════════════════════════════════════════════════

      const contract = new Contract(
        contractAddress, // Use chain-specific address
        marketplaceAbi,
        signer
      );

      let effectiveMintFee = configuredMintFee;
      let effectiveMintFeeEthText = mintFeeEthText;
      let effectiveTreasuryAddress = treasuryAddress || null;
      let onChainMintFee = null;
      let onChainTreasury = null;
      let onChainPlatformRoyaltyShareBps = null;
      let mintFeeMismatch = false;
      let treasuryMismatch = false;
      let platformRoyaltyShareMismatch = false;

      try {
        const [
          onChainMintFeeRaw,
          onChainTreasuryRaw,
          onChainPlatformRoyaltyShareRaw,
        ] = await Promise.all([
          contract.mintFeeWei(),
          contract.treasury(),
          contract.platformRoyaltyShareBps(),
        ]);

        onChainMintFee = BigInt(onChainMintFeeRaw.toString());
        onChainTreasury = onChainTreasuryRaw;
        onChainPlatformRoyaltyShareBps = Number(
          onChainPlatformRoyaltyShareRaw.toString()
        );

        if (onChainTreasury) {
          effectiveTreasuryAddress = onChainTreasury;
        }

        if (onChainMintFee !== configuredMintFee) {
          mintFeeMismatch = true;
          effectiveMintFee = onChainMintFee;
          effectiveMintFeeEthText =
            onChainMintFee > 0n ? formatEther(onChainMintFee) : null;
        }

        const configuredTreasury = treasuryAddress || "";
        if (
          onChainTreasury &&
          configuredTreasury &&
          onChainTreasury.toLowerCase() !== configuredTreasury.toLowerCase()
        ) {
          treasuryMismatch = true;
        }

        const configuredPlatformRoyaltyShare = Number(
          nftConfig.platformRoyaltyShareBps || 0
        );
        if (
          Number.isFinite(onChainPlatformRoyaltyShareBps) &&
          configuredPlatformRoyaltyShare !== onChainPlatformRoyaltyShareBps
        ) {
          platformRoyaltyShareMismatch = true;
        }
      } catch (mintFeeCheckError) {
        console.warn(
          "Unable to verify on-chain config prior to mint",
          mintFeeCheckError
        );
      }

      if (mintFeeMismatch || treasuryMismatch || platformRoyaltyShareMismatch) {
        toast.warn(
          "Contract config mismatch detected. Proceeding with on-chain values."
        );
        try {
          await addDoc(collection(null, "contractConfigMismatches"), {
            chainId: currentChainId,
            contractAddress,
            listingId: activeListingId,
            creatorWallet: account,
            configuredMintFeeWei: configuredMintFee.toString(),
            onChainMintFeeWei: onChainMintFee ? onChainMintFee.toString() : null,
            configuredTreasuryAddress: treasuryAddress || null,
            onChainTreasuryAddress: onChainTreasury || null,
            configuredPlatformRoyaltyShareBps: Number(
              nftConfig.platformRoyaltyShareBps || 0
            ),
            onChainPlatformRoyaltyShareBps,
            mintFeeMismatch,
            treasuryMismatch,
            platformRoyaltyShareMismatch,
            source: "mint",
            createdAt: serverTimestamp(),
          });
        } catch (logError) {
          console.warn(
            "Unable to log contract config mismatch to Firestore",
            logError
          );
        }
      }

      const effectiveMintFeeWeiString = effectiveMintFee.toString();

      await Promise.all([
        setDoc(
          mintedDocRef,
          {
            sourceCollection: sourceInfo.collectionName,
            sourcePath: sourceInfo.listingPath,
            mediaType: combined.mediaType || sourceInfo.mediaType || null,
            metadataIpfsUri: pinResponse.ipfsUri,
            metadataCid: pinResponse.cid,
            metadataGatewayUrl: pinResponse.gatewayUrl,
            metadataHash: pinResponse.metadataHash,
            metadataPinnedAt: serverTimestamp(),
            // Image IPFS fields (if available)
            imageIpfsUri: imagePinResult?.ipfsUri || null,
            imageCid: imagePinResult?.cid || null,
            imageGatewayUrl: imagePinResult?.gatewayUrl || null,
            imageSha256: imagePinResult?.sha256 || null,
            status: "metadata_ready",
            mintingStatus: "pending",
            chainId: currentChainId,
            mintFeeWei: effectiveMintFeeWeiString,
            treasuryAddress: effectiveTreasuryAddress || null,
            platformFeeBps: nftConfig.platformFeeBps,
            platformFeeRecipient: effectiveTreasuryAddress || null,
            swapFeeBps: nftConfig.swapFeeBps,
            creatorWallet: account,
            royaltyBps: appliedRoyaltyBps,
          },
          { merge: true }
        ),
        setDoc(
          sourceInfo.docRef,
          {
            metadataIpfsUri: pinResponse.ipfsUri,
            metadataCid: pinResponse.cid,
            metadataGatewayUrl: pinResponse.gatewayUrl,
            metadataHash: pinResponse.metadataHash,
            metadataPinnedAt: serverTimestamp(),
            imageIpfsUri: imagePinResult?.ipfsUri || null,
            imageCid: imagePinResult?.cid || null,
            imageGatewayUrl: imagePinResult?.gatewayUrl || null,
            imageSha256: imagePinResult?.sha256 || null,
            mintingStatus: "pending",
            chainId: currentChainId,
            mintFeeWei: effectiveMintFeeWeiString,
            treasuryAddress: effectiveTreasuryAddress || null,
            platformFeeBps: nftConfig.platformFeeBps,
            platformFeeRecipient: effectiveTreasuryAddress || null,
            swapFeeBps: nftConfig.swapFeeBps,
            creatorWallet: account,
            royaltyBps: appliedRoyaltyBps,
          },
          { merge: true }
        ),
      ]);

      const overrides = effectiveMintFee > 0n ? { value: effectiveMintFee } : {};

      toast.info("Confirm the mint transaction in MetaMask to complete.");

      let mintTxHash;
      let mintedTokenId;
      let actualContractAddress = contractAddress; // Default to shared marketplace
      let splitterAddress = null; // RoyaltySplitter address (shared marketplace mints only)

      if (useCustomCollection && customCollectionAddress) {
        // Mint to custom collection
        console.log("[MINT] Minting to custom collection:", customCollectionAddress);
        const mintResult = await mintToCollection({
          signer,
          collectionAddress: customCollectionAddress,
          recipient: account,
          tokenURI: pinResponse.ipfsUri,
        });
        mintTxHash = mintResult.txHash;
        mintedTokenId = mintResult.tokenId;
        actualContractAddress = customCollectionAddress;
      } else {
        // Mint to shared Unit Marketplace
        const txResponse = await contract.mintTo(
          account,
          pinResponse.ipfsUri,
          appliedRoyaltyBps,
          overrides
        );
        mintTxHash = txResponse.hash;
        const receipt = await txResponse.wait();

        // Capture RoyaltySplitter address for shared marketplace mints
        splitterAddress = extractSplitterAddressFromReceipt(receipt, contract.interface);

        mintedTokenId = extractTokenIdFromReceipt(
          receipt,
          contract.interface
        );
        if (!mintedTokenId) {
          try {
            const nextTokenId = await contract.nextTokenId();
            const numeric = BigInt(nextTokenId.toString()) - BigInt(1);
            mintedTokenId = numeric.toString();
          } catch (idError) {
            if (idError?.code === "BAD_DATA" || idError?.code === "CALL_EXCEPTION") {
              console.info(
                "Unit marketplace ABI does not expose nextTokenId; minted tokenId will be hydrated from metadata later.",
                idError
              );
            } else {
              console.warn("Unable to infer tokenId from receipt", idError);
            }
          }
        }
      }

      const mintFeeWeiPaid = effectiveMintFee.toString();
      const mintFeeEthFormatted = effectiveMintFeeEthText || "0";

      // splitterAddress is only defined for shared marketplace mints (not custom collection)
      const royaltySplitterAddr = splitterAddress || null;

      await Promise.all([
        setDoc(
          mintedDocRef,
          {
            tokenId: mintedTokenId || null,
            contractAddress: actualContractAddress,
            isCustomCollection: useCustomCollection,
            chainId: currentChainId,
            mintTxHash,
            mintedAt: serverTimestamp(),
            creatorWallet: account,
            status: "minted",
            mintingStatus: "success",
            mintFeeWei: mintFeeWeiPaid,
            mintFeeEth: mintFeeEthFormatted,
            treasuryAddress: effectiveTreasuryAddress || null,
            platformFeeBps: nftConfig.platformFeeBps,
            platformFeeRecipient: effectiveTreasuryAddress || null,
            swapFeeBps: nftConfig.swapFeeBps,
            royaltyBps: appliedRoyaltyBps,
            royaltySplitterAddress: royaltySplitterAddr || null,
            imageIpfsUri: imagePinResult?.ipfsUri || null,
            imageCid: imagePinResult?.cid || null,
            imageGatewayUrl: imagePinResult?.gatewayUrl || null,
            imageSha256: imagePinResult?.sha256 || null,
          },
          { merge: true }
        ),
        setDoc(
          sourceInfo.docRef,
          {
            contractAddress: actualContractAddress,
            isCustomCollection: useCustomCollection,
            chainId: currentChainId,
            tokenId: mintedTokenId || null,
            mintTxHash,
            onChainStatus: "minted",
            mintingStatus: "success",
            mintedAt: serverTimestamp(),
            mintFeeWei: mintFeeWeiPaid,
            mintFeeEth: mintFeeEthFormatted,
            treasuryAddress: effectiveTreasuryAddress || null,
            platformFeeBps: nftConfig.platformFeeBps,
            platformFeeRecipient: effectiveTreasuryAddress || null,
            swapFeeBps: nftConfig.swapFeeBps,
            royaltyBps: appliedRoyaltyBps,
            royaltySplitterAddress: royaltySplitterAddr || null,
            imageIpfsUri: imagePinResult?.ipfsUri || null,
            imageCid: imagePinResult?.cid || null,
            imageGatewayUrl: imagePinResult?.gatewayUrl || null,
            imageSha256: imagePinResult?.sha256 || null,
          },
          { merge: true }
        ),
      ]);

      markMinted(processedItems, mintedCache, listingId, {
        metadataIpfsUri: pinResponse.ipfsUri,
        metadataCid: pinResponse.cid,
        metadataGatewayUrl: pinResponse.gatewayUrl,
        metadataHash: pinResponse.metadataHash,
        metadataListingPath: sourceInfo.listingPath,
        imageIpfsUri: imagePinResult?.ipfsUri || null,
        imageCid: imagePinResult?.cid || null,
        imageGatewayUrl: imagePinResult?.gatewayUrl || null,
        imageSha256: imagePinResult?.sha256 || null,
        contractAddress: actualContractAddress,
        isCustomCollection: useCustomCollection,
        tokenId: mintedTokenId || null,
        onChainStatus: "minted",
        mintFeeWei: mintFeeWeiPaid,
        mintFeeEth: mintFeeEthFormatted,
        treasuryAddress: effectiveTreasuryAddress || null,
        platformFeeBps: nftConfig.platformFeeBps,
        platformFeeRecipient: effectiveTreasuryAddress || null,
        swapFeeBps: nftConfig.swapFeeBps,
        creatorWallet: account,
        royaltyBps: appliedRoyaltyBps,
      });

      // --- Post-mint: mark itemsTimeline doc as NFT listing ---
      const timelineDocId = pendingMint.value?.item?._timelineId;
      const mintSaleConfig = pendingMint.value?.saleConfig;
      const salePrice = mintSaleConfig?.sale?.enabled ? (mintSaleConfig.sale.price || 0) : 0;
      const saleCurrency = mintSaleConfig?.sale?.currency || 'ETH';
      const uid = currentUserUid?.value || currentUserUid;

      if (timelineDocId) {
        try {
          await setDoc(doc(null, "itemsTimeline", timelineDocId), {
            isNFTListing: true,
            listingStatus: "active",
            listingMode: "in_place",
            nftId: mintedTokenId
              ? `nft-${currentChainId}-${actualContractAddress}-${mintedTokenId}`
              : null,
            contractAddress: actualContractAddress,
            tokenId: mintedTokenId || null,
            chainId: currentChainId,
            mintTxHash,
            onChainStatus: "minted",
            listingPrice: salePrice,
            currency: saleCurrency,
            coinSymbol: saleCurrency,
            requiresWalletPurchase: true,
            listedAt: serverTimestamp(),
            lastUpdated: serverTimestamp(),
          }, { merge: true });
          console.log("[useMarketplaceMinting] Updated itemsTimeline with NFT listing fields:", timelineDocId);
        } catch (tlErr) {
          console.warn("[useMarketplaceMinting] Failed to update timeline doc with listing fields", tlErr);
        }

        // Create nftListings tracking doc ONLY if tokenId is known
        if (mintedTokenId) {
          const nftId = `nft-${currentChainId}-${actualContractAddress}-${mintedTokenId}`;
          try {
            await setDoc(doc(null, "nftListings", nftId), {
              nftId,
              contractAddress: actualContractAddress,
              tokenId: mintedTokenId,
              chainId: currentChainId,
              timelineDocId,
              listingStatus: "active",
              listingMode: "in_place",
              listingPrice: salePrice,
              listingPriceWei: salePrice > 0 ? parseUnits(salePrice.toString(), 18).toString() : "0",
              currency: saleCurrency,
              currentOwner: account,
              ownerUid: uid,
              listedAt: serverTimestamp(),
              lastUpdated: serverTimestamp(),
            });
            console.log("[useMarketplaceMinting] Created nftListings tracking doc:", nftId);
          } catch (nlErr) {
            console.warn("[useMarketplaceMinting] Failed to create nftListings tracking doc", nlErr);
          }
        }
      }

      toast.success("Mint successful! The NFT is now on-chain.");
      showMintModal.value = false;
      pendingMint.value = null;
    } catch (error) {
      console.error("Failed to mint listing", error);
      
      // Provide user-friendly error messages
      if (error?.code === "INSUFFICIENT_FUNDS" || error?.code === -32003) {
        const chainName = currentChainId === "0x1" ? "Ethereum" 
          : currentChainId === "0x2105" ? "Base" 
          : "current network";
        let configuredMintFeeEth = null;
        try {
          configuredMintFeeEth = formatEther(configuredMintFee);
        } catch (feeFormatError) {
          console.warn("Unable to format configured mint fee", feeFormatError);
        }
        const feeText = configuredMintFeeEth
          ? `${configuredMintFeeEth} ETH`
          : "the configured mint fee";
        toast.error(
          `Insufficient funds on ${chainName}. You need at least ${feeText} plus gas fees to mint.`
        );
      } else if (error?.code === 4001 || error?.code === "ACTION_REJECTED") {
        toast.info("Transaction cancelled. You can try minting again when you're ready.");
      } else {
        toast.error(error?.message || "Mint failed. Try again later.");
      }
      
      // Don't write failure to Firestore for user rejections — nothing to roll back
      const isUserRejection = error?.code === 4001 || error?.code === "ACTION_REJECTED";

      // Mark minting as failed in Firestore to prevent false "minted" state
      if (mintedDocRef && sourceInfo?.docRef && !isUserRejection) {
        try {
          await Promise.all([
            setDoc(
              mintedDocRef,
              {
                mintingStatus: "failed",
                chainId: currentChainId,
                mintingError: error?.message || "Unknown error",
                mintingErrorCode: error?.code || null,
                mintingFailedAt: serverTimestamp(),
              },
              { merge: true }
            ),
            setDoc(
              sourceInfo.docRef,
              {
                mintingStatus: "failed",
                chainId: currentChainId,
                mintingError: error?.message || "Unknown error",
                mintingErrorCode: error?.code || null,
                mintingFailedAt: serverTimestamp(),
              },
              { merge: true }
            ),
          ]);
        } catch (updateError) {
          console.error("Failed to update minting failure status", updateError);
        }
      }
      
      // Toasts already shown above per error type — no generic fallback needed
    } finally {
      mintingStatus[activeListingId] = false;
      mintModalLoading.value = false;
      mintModalAcknowledged.value = false;
    }
  };

  const setPendingRoyaltyBps = (value) => {
    const clamped = clampRoyaltyBps(value);
    if (!pendingMint.value) {
      return clamped;
    }

    pendingMint.value.royaltyBps = clamped;

    const currentMetadata = pendingMint.value.metadata || {};
    pendingMint.value.metadata = {
      ...currentMetadata,
      royaltyBps: clamped,
      properties: {
        ...(currentMetadata.properties || {}),
        royaltyBps: clamped,
      },
    };

    return clamped;
  };

  /**
   * Set sale configuration for the pending mint
   * Called by SimpleMintModal when user confirms
   * @param {Object} config - MintConfig from SimpleMintModal
   */
  const setSaleConfig = (config) => {
    if (!pendingMint.value) {
      console.warn("[useMarketplaceMinting] No pending mint to set sale config");
      return;
    }

    pendingMint.value.saleConfig = config;
    
    // Update chain if different
    if (config?.chainId) {
      pendingMint.value.selectedChainId = config.chainId;
    }

    // Update royalty if provided
    if (config?.royaltyBps !== undefined) {
      setPendingRoyaltyBps(config.royaltyBps);
    }

    console.log("[useMarketplaceMinting] Sale config set:", config);
  };

  /**
   * Burn (destroy) an NFT permanently
   * Works for both UnitMarketplace and UnitCollection contracts
   * For legacy contracts without burn(), falls back to transferring to burn address
   * @param {Object} item - The item to burn (must have contractAddress and tokenId)
   * @returns {Promise<Object>} { success, txHash, error?, method? }
   */
  const burnNFT = async (item) => {
    // Standard burn address (0x...dEaD)
    const BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD";
    
    if (!item) {
      return { success: false, error: "No item provided" };
    }

    // Try multiple locations where mint info might be stored
    let tokenId = item.mintingData?.mintInfo?.tokenId || item.mintInfo?.tokenId || item.tokenId;
    let contractAddress = item.mintingData?.mintInfo?.contractAddress || item.mintInfo?.contractAddress || item.contractAddress;
    let chainId = item.mintingData?.mintInfo?.chainId || item.mintInfo?.chainId || item.chainId;

    console.log("[useMarketplaceMinting] burnNFT resolving mint info:", {
      itemId: item.id,
      resolvedTokenId: tokenId,
      resolvedContractAddress: contractAddress,
      resolvedChainId: chainId,
      itemKeys: Object.keys(item),
    });

    if (!tokenId || !contractAddress) {
      return { success: false, error: "Item has no mint info (tokenId/contractAddress) - cannot burn unminted items" };
    }

    console.log("[useMarketplaceMinting] burnNFT called:", {
      tokenId,
      contractAddress,
      chainId,
      itemId: item.id,
    });

    try {
      // Get wallet signer using the existing helper
      const { provider, signer, account } = await ensureWalletConnected(chainId);
      
      if (!signer) {
        return { success: false, error: "Failed to get wallet signer" };
      }

      // Extended ABI that includes both burn and transferFrom for fallback
      const burnAbi = [
        "function burn(uint256 tokenId) external",
        "function ownerOf(uint256 tokenId) view returns (address)",
        "function transferFrom(address from, address to, uint256 tokenId) external",
        "function safeTransferFrom(address from, address to, uint256 tokenId) external",
      ];
      
      const contract = new Contract(contractAddress, burnAbi, signer);
      
      // Check current on-chain owner - with retry for recently minted NFTs
      let owner;
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries) {
        try {
          owner = await contract.ownerOf(tokenId);
          break; // Success, exit loop
        } catch (ownerError) {
          retries++;
          console.warn(`[useMarketplaceMinting] ownerOf failed (attempt ${retries}/${maxRetries}):`, ownerError.message);
          
          if (retries >= maxRetries) {
            // Check if it's an "invalid token ID" error - token might not exist
            if (ownerError.message?.includes("invalid token ID") || ownerError.message?.includes("nonexistent token")) {
              return { success: false, error: "Token does not exist on-chain. It may have already been burned or was never minted." };
            }
            throw ownerError;
          }
          
          // Wait before retry - node may not have synced the mint yet
          console.log(`[useMarketplaceMinting] Waiting 2s before retry...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      const ownerLower = owner.toLowerCase();
      const accountLower = account.toLowerCase();
      
      // If already sent to burn address, return special status so UI can just delete from Firestore
      if (ownerLower === BURN_ADDRESS.toLowerCase()) {
        return { 
          success: true, 
          alreadyBurned: true,
          method: "already-burned",
          tokenId: tokenId.toString(),
        };
      }
      
      // Verify caller owns the token
      if (ownerLower !== accountLower) {
        return { success: false, error: `You do not own this NFT. Current owner: ${owner}` };
      }

      // Try burn() first, fallback to transfer to burn address for legacy contracts
      let tx;
      let method = "burn";
      
      try {
        // Estimate gas for burn first to check if function exists
        const estimatedGas = await contract.burn.estimateGas(tokenId);
        console.log("[useMarketplaceMinting] Burn estimated gas:", estimatedGas.toString());
        
        // Execute burn
        tx = await contract.burn(tokenId);
        console.log("[useMarketplaceMinting] Burn tx sent:", tx.hash);
      } catch (burnError) {
        // If burn fails (likely function doesn't exist), fallback to transfer to burn address
        console.warn("[useMarketplaceMinting] burn() failed, falling back to transfer to burn address:", burnError.message);
        
        // Check if it's a "function not found" type error or revert
        const isBurnNotSupported = 
          burnError.message?.includes("execution reverted") ||
          burnError.message?.includes("missing revert data") ||
          burnError.message?.includes("CALL_EXCEPTION") ||
          burnError.code === "CALL_EXCEPTION";
        
        if (!isBurnNotSupported) {
          // Re-throw if it's a different kind of error (user rejection, etc)
          throw burnError;
        }
        
        method = "transfer-to-burn-address";
        console.log("[useMarketplaceMinting] Transferring NFT to burn address:", BURN_ADDRESS);
        
        // Estimate gas for transfer
        const transferGas = await contract.transferFrom.estimateGas(account, BURN_ADDRESS, tokenId);
        console.log("[useMarketplaceMinting] Transfer estimated gas:", transferGas.toString());
        
        // Execute transfer to burn address
        tx = await contract.transferFrom(account, BURN_ADDRESS, tokenId);
        console.log("[useMarketplaceMinting] Transfer to burn address tx sent:", tx.hash);
      }

      const receipt = await tx.wait();
      console.log("[useMarketplaceMinting] Burn/Transfer confirmed, gas used:", receipt.gasUsed.toString());

      // Clear from minted cache if present
      try {
        if (mintedCache?.value && mintedCache.value[item.id]) {
          delete mintedCache.value[item.id];
        }
      } catch (cacheError) {
        console.warn("[useMarketplaceMinting] Failed to clear minted cache:", cacheError);
      }

      return {
        success: true,
        txHash: tx.hash,
        tokenId: tokenId.toString(),
        method, // "burn" or "transfer-to-burn-address"
      };
    } catch (error) {
      console.error("[useMarketplaceMinting] Burn failed:", error);
      
      // Parse common errors
      let errorMessage = error.message || "Burn failed";
      
      // Check if token doesn't exist - this means we can safely delete from Firestore
      const isTokenNonexistent = 
        errorMessage.includes("invalid token ID") ||
        errorMessage.includes("nonexistent token") ||
        errorMessage.includes("ERC721: owner query for nonexistent token");
      
      if (isTokenNonexistent) {
        // Token doesn't exist on-chain - return success so UI can clean up Firestore
        console.log("[useMarketplaceMinting] Token doesn't exist on-chain, allowing Firestore cleanup");
        return {
          success: true,
          alreadyBurned: true,
          method: "token-nonexistent",
          tokenId: tokenId.toString(),
        };
      }
      
      if (error.code === "ACTION_REJECTED" || error.code === 4001) {
        errorMessage = "Transaction rejected by user";
      } else if (errorMessage.includes("caller is not owner")) {
        errorMessage = "You do not own this NFT";
      }

      return { success: false, error: errorMessage };
    }
  };

  return {
    mintingStatus,
    mintedCache,
    showMintModal,
    pendingMint,
    mintModalAcknowledged,
    mintModalLoading,
    hasMintFeeConfigured,
    mintFeeEthDisplay,
    canMintItem,
    isItemMinted,
    isItemOwnedByCurrentUser,
    startMint,
    confirmMint,
    cancelMint,
    setPendingRoyaltyBps,
    setSaleConfig,
    burnNFT,
  };
}
